// AGC: AppHeader - Global app header with hamburger, logo, notifications
// AGC: Features:
// AGC: - Left: Hamburger menu button (green icon)
// AGC: - Center: DroidFarm logo (light mode: topmenulogo.png, dark mode: logologin.png)
// AGC: - Right: Notifications bell icon (green icon)
// AGC: - Light background matching page background
// AGC: - No borders or shadows for seamless blend

import React from 'react'
import { Menu, Bell } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// AGC: Dedicated WhatsApp agent senders (digits only for wa.me links).
const WA_AGENT_NUMBER_BR = '554192709559'
const WA_AGENT_NUMBER_AR = '5492214974643'

interface AppHeaderProps {
  onMenuClick: () => void
  onNotificationsClick: () => void
  notificationCount?: number
}

/**
 * AGC: AppHeader - Global header bar for all main pages
 * AGC: Provides consistent navigation entry point and app identity
 * AGC: Updated design: seamless background blend, no borders/shadows, dual logo for light/dark mode
 */
export const AppHeader: React.FC<AppHeaderProps> = ({
  onMenuClick,
  onNotificationsClick,
  notificationCount = 0,
}) => {
  const { t, i18n } = useTranslation()
  // AGC: es users reach the dedicated AR agent number; everyone else the BR number.
  const isEs = (i18n.language || '').toLowerCase().startsWith('es')
  const waNumber = isEs ? WA_AGENT_NUMBER_AR : WA_AGENT_NUMBER_BR
  const waHref = `https://wa.me/${waNumber}?text=${encodeURIComponent(t('common.whatsappAgentPrefill'))}`
  return (
    <header
      className="
        sticky top-0 z-40
        bg-[#F4F5F7] dark:bg-gray-900
        transition-colors
      "
      // AGC: Own safe-area-inset-top so the header bg fills the Android status
      // AGC: bar region — otherwise the MainLayout wrapper's slightly different
      // AGC: bg (#fafbfc) showed as a visible color band above the logo.
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="flex items-center justify-between h-14 px-4">
        {/* AGC: Left - Hamburger Button (green icon) */}
        <button
          onClick={onMenuClick}
          className="
            flex items-center justify-center
            w-10 h-10
            rounded-lg
            hover:bg-white/50 dark:hover:bg-gray-800
            active:scale-95
            transition-all duration-200
          "
          aria-label="Open navigation menu"
        >
          <Menu className="w-6 h-6 text-[#065F46] dark:text-emerald-400" />
        </button>

        {/* AGC: Center - Logo Images (light mode: green logo, dark mode: white logo) */}
        <img 
          src="/topmenulogo.png" 
          alt="DroidFarm" 
          className="h-8 w-auto object-contain dark:hidden"
        />
        <img 
          src="/logologin.png" 
          alt="DroidFarm" 
          className="h-8 w-auto object-contain hidden dark:block"
        />

        {/* AGC: Right - WhatsApp + Notifications */}
        <div className="flex items-center gap-1">
        {/* AGC: WhatsApp button - opens chat with pre-filled message */}
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          className="
            relative
            flex items-center justify-center
            w-10 h-10
            rounded-lg
            hover:bg-white/50 dark:hover:bg-gray-800
            active:scale-95
            transition-all duration-200
          "
          aria-label="WhatsApp"
        >
          <img src="/WhatsApp_icon.png" alt="WhatsApp" className="w-7 h-7" />
        </a>
        <button
          onClick={onNotificationsClick}
          className="
            relative
            flex items-center justify-center
            w-10 h-10
            rounded-lg
            hover:bg-white/50 dark:hover:bg-gray-800
            active:scale-95
            transition-all duration-200
          "
          aria-label={`Notifications${notificationCount > 0 ? ` (${notificationCount} unread)` : ''}`}
        >
          <Bell className="w-6 h-6 text-[#065F46] dark:text-emerald-400" />
          {notificationCount > 0 && (
            <span
              className="
                absolute top-1 right-1
                min-w-[18px] h-[18px]
                flex items-center justify-center
                bg-red-500 text-white
                text-xs font-semibold
                rounded-full
                px-1
              "
            >
              {notificationCount > 99 ? '99+' : notificationCount}
            </span>
          )}
        </button>
        </div>
      </div>
    </header>
  )
}
